import { Button, Grid } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import * as React from 'react';
import { useContext, useState } from 'react';
import { BulkResponseDialog } from '../common/BulkResponseDialog';
import { AuthContext } from '../_auth/AuthContext';
import { FreezeMemberships } from '../_service/bulk';
import { BulkUtilResponse } from '../_types/bulk';
import { ISearchResult } from '../_types/search';
import { ServicePlan } from '../_types/serviceplan';
import { ILocationSummary } from '../_types/user';
import { stupidDateFormat } from '../_util/Utility';
import { PlanByLocationSelector } from '../common/Selector/PlanByLocationSelector';
import { BulkShowResultsListButton } from '../common/BulkShowResultsListButton';


interface Props {
  plans: ServicePlan[],
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dateContainer: {
      margin: 10
    }
  })
);

const FreezeMembers: React.FC<Props> = (prop: Props) => {
  const { user } = useContext(AuthContext);
  const [location, setLocation] = useState<ILocationSummary | ISearchResult | null>(user.currentLocation || null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>();
  const [isFreezing, setIsFreezing] = useState<boolean>(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [ignoreList, setIgnoreList] = useState<string[]>([]);
  const [storedEmailList, setStoredEmailList] = useState<string[] | undefined>();
  const [storedIgnoreList, setStoredIgnoreList] = useState<string[] | undefined>();

  const classes = useStyles();
  
  let monthFromNow = new Date();
  monthFromNow.setMonth(monthFromNow.getMonth() + 1);
  const [minDate] = useState<string>(stupidDateFormat(monthFromNow));
  const [unfreezeDate, setUnfreezeDate] = useState<string>(minDate);

  const _freeze = async () => {
    if (!location) return alert('Please select a location.');
    if (!unfreezeDate) return alert('Please select a date to unfreeze.');
    setIsFreezing(true);
    let response: BulkUtilResponse | null;
    
    try {
        response = await FreezeMemberships(location?.id, 
          new Date(unfreezeDate), 
          (selectedPlan && selectedPlan !== 'all plans') ? selectedPlan : null);
    } catch (error) {
      setIsFreezing(false);
      return alert('Something went wrong while connecting to the API. Please try again.');
    }

      if (response !== null) {
        setIsFreezing(false);
        setSelectedPlan(null);

        if (response.emails && response.emails.length > 0) {
          setEmailList(response.emails);
          setIsOpen(true);
        }
        
        if (response.ignoreList && response.ignoreList.length > 0) {
          setIgnoreList(response.ignoreList);
          setIsOpen(true);
        }
  
        if (response.emails && response.emails.length === 0
          && response.ignoreList && response.ignoreList.length === 0) {
            return alert('No memberships were frozen. Make sure you selected the correct location and/or plans.');
          }

      }
  }

  return (
    <>
            <Grid item>
              <h3>Freeze Members</h3>
              <p>Select a location and a plan to freeze the members of a given plan. You can select "All Plans" to freeze all members at the given location. 
                Then select a date you wish the members to be automatically unfrozen.</p>
            </Grid>
            <PlanByLocationSelector
              onLocationChange={(newValue: ISearchResult | null) => {
                setLocation(newValue);
              }}
              onPlanChange={(event: any) => setSelectedPlan(event.target.value as string)}
              planValue={selectedPlan || ''} 
              planInputLabel='Plans'
              plans={prop.plans}
            />
            <Grid item xs={12} sm={12} className={classes.dateContainer}>
              <label>Date to unfreeze: </label>
              <input type="date" 
                min={minDate}
                value={unfreezeDate}
                onChange={(event) => setUnfreezeDate(event.target.value as string)} />
            </Grid>
            {isFreezing ? <p>Freezing, please wait... This can take several minutes...</p> :
              <>
                <Grid item xs={12} sm={12} style={{ display: "flex", justifyContent: "flex-end", height: "2.85em", flexDirection: "column" }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => _freeze()}>
                    Freeze
                  </Button>
                </Grid>
                <Grid item xs={12} sm={12} style={{ display: "flex", justifyContent: "flex-start", flexDirection: "column" }}>
                  <BulkShowResultsListButton
                    title={"Successfully Froze " + (storedEmailList?.length || 0).toString() + " Member Emails"}
                    emailList={storedEmailList}
                    ignoreList={storedIgnoreList}
                    hide={storedEmailList === undefined}
                    csvName={"FreezeMembers" + new Date().toLocaleDateString()}
                  />
                </Grid>
              </>
            }
            {(emailList.length > 0 || ignoreList.length > 0) && <BulkResponseDialog 
              title="Successfully Frozen Member Emails"
              isOpen={isOpen}
              emailList={emailList}
              ignoreList={ignoreList}
              handleClose={() => {
                setIsOpen(false);
                setStoredEmailList(emailList);
                setStoredIgnoreList(ignoreList);
                setEmailList([]);
                setIgnoreList([]);
              }} />}
    </>
  );
}

export default FreezeMembers;